/**
 * PostHog analytics + funil + session replay (free tier 1M events/mes).
 *
 * Carrega lazy em window.posthog. Se VITE_POSTHOG_KEY vazio, no-op.
 *
 * Eventos chave:
 *   - signup_started / signup_completed
 *   - login_succeeded / login_failed
 *   - terms_accepted
 *   - booking_started / booking_step_completed
 *   - payment_method_selected
 *   - checkout_redirected
 *   - transfer_proof_uploaded
 *   - account_data_exported / account_deletion_requested
 */

type PostHog = {
  init: (key: string, opts: Record<string, unknown>) => void;
  capture: (event: string, props?: Record<string, unknown>) => void;
  identify: (id: string, traits?: Record<string, unknown>) => void;
  reset: () => void;
  opt_out_capturing: () => void;
  opt_in_capturing: () => void;
};

declare global {
  interface Window {
    posthog?: PostHog;
  }
}

let loaded = false;

export function initPostHog(): void {
  if (loaded) return;
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://eu.i.posthog.com';
  if (!key) return;

  // Snippet oficial PostHog (sem npm pra reduzir bundle)
  // https://posthog.com/docs/libraries/js
  ((p: unknown, ...rest: unknown[]) => {
    const _p = p as { __SV?: number };
    if (window.posthog && _p?.__SV) return;
    const ph = (window.posthog = {} as PostHog);
    const proxy = ['capture', 'identify', 'reset', 'init', 'opt_out_capturing', 'opt_in_capturing'];
    proxy.forEach((m) => {
      // @ts-expect-error queueing pre-init calls
      ph[m] = (...args: unknown[]) => ((ph._q ??= []) as unknown[]).push([m, ...args]);
    });
    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = `${host}/static/array.js`;
    const f = document.getElementsByTagName('script')[0];
    f?.parentNode?.insertBefore(s, f);
    void rest;
  })(window.posthog);

  window.posthog?.init(key, {
    api_host: host,
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: false,
    persistence: 'localStorage+cookie',
    session_recording: { maskAllInputs: true, maskTextSelector: '.dp-private' },
    loaded: () => {
      loaded = true;
    },
  });
}

export function track(event: string, props?: Record<string, unknown>): void {
  try {
    window.posthog?.capture(event, props);
  } catch {
    // no-op: analytics nunca quebra UI
  }
}

export function identify(userId: string, traits?: Record<string, unknown>): void {
  try {
    window.posthog?.identify(userId, traits);
  } catch {
    // no-op
  }
}

export function resetAnalyticsUser(): void {
  try {
    window.posthog?.reset();
  } catch {
    // no-op
  }
}

export function optOutAnalytics(): void {
  try {
    window.posthog?.opt_out_capturing();
  } catch {
    // no-op
  }
}
